import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, LogOut, UserCircle, Calendar, Loader2, Video, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { user, isAuthenticated, loading, logout } = useAuth();

  // Lock body scroll when mobile menu or user menu is open
  useBodyScrollLock(isMobileMenuOpen || isUserMenuOpen);

  const navLinks = [
    { name: 'Listings', path: '/' },
    { name: 'Consultation', path: '/consultation' },
  ];

  // Check if current page needs navbar to always be visible (pages with dark headers or booking details)
  const needsAlwaysVisibleNavbar = 
    location.pathname.startsWith('/bookings') || 
    location.pathname.startsWith('/schools/');

  // Handle scroll for glassmorphism effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const mobileMenuPortal =
    typeof document !== 'undefined'
      ? createPortal(
          <AnimatePresence>
            {isMobileMenuOpen && (
              <>
                <motion.div
                  className="md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm"
                  style={{ zIndex: 9999 }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => setIsMobileMenuOpen(false)}
                />
                <motion.div
                  className="md:hidden fixed inset-x-0 top-0 pt-20 pb-6"
                  style={{ zIndex: 10000 }}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                >
                  <div className="mx-4 bg-white rounded-2xl shadow-2xl border border-white/30 overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-4 border-b border-midnight/10">
                      <span className="text-2 font-semibold tracking-wide text-midnight">
                        Menu
                      </span>
                      <button
                        className="p-2 rounded-lg hover:bg-fog transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                        aria-label="Close menu"
                      >
                        <X className="h-5 w-5 text-midnight/70" />
                      </button>
                    </div>
                    <div className="p-4 space-y-2">
                      {navLinks.map((link, index) => (
                        <motion.div
                          key={link.path}
                          initial={{ opacity: 0, x: -16 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -16 }}
                          transition={{ duration: 0.2, delay: index * 0.05 }}
                        >
                          <Link
                            to={link.path}
                            className={`block px-4 py-3 rounded-xl text-2 font-semibold tracking-wide uppercase transition-all duration-300 ${
                              location.pathname === link.path
                                ? 'bg-orange text-white shadow-md'
                                : 'text-midnight/80 hover:bg-fog hover:text-midnight'
                            }`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {link.name}
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                    <div className="p-4 pt-0">
                      <Link
                        to="/"
                        className="block px-4 py-3 bg-orange text-white rounded-xl text-2 font-semibold text-center hover:bg-orange-dark transition-all duration-300 shadow-sm"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Find a Course
                      </Link>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>,
          document.body
        )
      : null;

  return (
    <>
      {/* Mobile User Dropdown Backdrop - Rendered outside nav for proper z-index */}
      <AnimatePresence>
        {isUserMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 99
            }}
            onClick={() => setIsUserMenuOpen(false)}
          />
        )}
      </AnimatePresence>

    <nav 
      className={`fixed w-full z-[1000] transition-all duration-300 ${
        isScrolled || needsAlwaysVisibleNavbar
          ? 'bg-white/80 backdrop-blur-xl shadow-sm border-b border-white/20' 
          : 'bg-transparent'
      }`}
    >
      <div className="w-full max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Left */}
          <Link to="/" className="flex items-center justify-center h-full flex-shrink-0">
            <div style={{ width: '170px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img 
                src="/images/logo.png" 
                alt="BookMyYogaTTC Logo" 
                style={{ 
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </div>
          </Link>

          {/* Desktop Navigation - Center */}
          <div className="hidden md:flex items-center space-x-12 absolute left-1/2 transform -translate-x-1/2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-1 font-medium tracking-widest uppercase transition-colors duration-300 ${
                  location.pathname === link.path
                    ? 'text-midnight font-semibold'
                    : 'text-midnight/60 hover:text-midnight'
                }`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <motion.div
                    className="absolute -bottom-1 left-0 w-full h-0.5 bg-orange"
                    layoutId="underline"
                  />
                )}
              </Link>
            ))}
          </div>

          {/* CTA Button - Right */}
          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/"
              className="px-6 py-2.5 bg-orange text-white rounded-full text-2 font-medium hover:bg-orange-dark transition-all duration-300 shadow-sm hover:shadow-md"
            >
              Find a Course
            </Link>

            {/* User Icon (Desktop) */}
            <div className="hidden md:flex items-center relative" ref={userMenuRef}>
              <button
                onClick={() => !loading && setIsUserMenuOpen(!isUserMenuOpen)}
                disabled={loading}
                className={`relative w-10 h-10 rounded-full transition-all duration-300 flex items-center justify-center ${
                  loading
                    ? 'bg-fog-dark cursor-wait'
                    : isUserMenuOpen 
                      ? 'bg-midnight/5' 
                      : 'bg-fog hover:bg-fog-dark'
                }`}
                aria-label="User menu"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 text-midnight/40 animate-spin" />
                ) : (
                  <User className={`h-5 w-5 transition-colors duration-300 ${
                    isUserMenuOpen ? 'text-orange' : 'text-midnight/70'
                  }`} />
                )}
              </button>

              {/* User Dropdown Menu */}
              <AnimatePresence>
                {isUserMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 top-full mt-3 w-64 bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 py-2 z-50"
                  >
                    {loading ? (
                      <div className="px-4 py-3 text-2 text-midnight/60 text-center">
                        Loading...
                      </div>
                    ) : isAuthenticated && user ? (
                      <>
                        <div className="px-4 py-3 border-b border-midnight/10">
                          <p className="text-2 font-semibold text-midnight">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-1 text-midnight/50 truncate">{user.email}</p>
                        </div>
                        <Link
                          to="/profile"
                          className="flex items-center px-4 py-3 text-2 font-medium text-midnight/70 hover:bg-fog transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <UserCircle className="h-4 w-4 mr-3" />
                          View Profile
                        </Link>
                        <Link
                          to="/bookings"
                          className="flex items-center px-4 py-3 text-2 font-medium text-midnight/70 hover:bg-fog transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Calendar className="h-4 w-4 mr-3" />
                          My Bookings
                        </Link>
                        <Link
                          to="/my-consultations"
                          className="flex items-center px-4 py-3 text-2 font-medium text-midnight/70 hover:bg-fog transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <Video className="h-4 w-4 mr-3" />
                          My Consultations
                        </Link>
                        <div className="border-t border-midnight/10 my-1"></div>
                        <button
                          onClick={async () => {
                            setIsUserMenuOpen(false);
                            await logout();
                            navigate('/');
                          }}
                          className="w-full flex items-center px-4 py-3 text-2 font-medium text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <LogOut className="h-4 w-4 mr-3" />
                          Logout
                        </button>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/login"
                          className="block px-4 py-3 text-2 font-medium text-midnight/70 hover:bg-fog transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Login
                        </Link>
                        <Link
                          to="/signup"
                          className="block px-4 py-3 text-2 font-medium text-midnight/70 hover:bg-fog transition-colors"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          Sign Up
                        </Link>
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Mobile - User Icon and Menu Button */}
          <div className="md:hidden flex items-center space-x-3">
            <button
              onClick={() => !loading && setIsUserMenuOpen(!isUserMenuOpen)}
              disabled={loading}
              className={`relative w-10 h-10 rounded-full transition-all duration-300 flex items-center justify-center ${
                loading
                  ? 'bg-fog-dark cursor-wait'
                  : isUserMenuOpen 
                    ? 'bg-midnight/5' 
                    : 'bg-fog hover:bg-fog-dark'
              }`}
              aria-label="User menu"
            >
              {loading ? (
                <Loader2 className="h-5 w-5 text-midnight/40 animate-spin" />
              ) : (
                <User className={`h-5 w-5 transition-colors duration-300 ${
                  isUserMenuOpen ? 'text-orange' : 'text-midnight/70'
                }`} />
              )}
            </button>
            <button
              className="flex flex-col justify-center items-center w-10 h-10 bg-fog rounded-lg hover:bg-fog-dark transition-all duration-300 z-50 relative"
              onClick={() => {
                setIsUserMenuOpen(false);
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              aria-label="Toggle mobile menu"
            >
            <motion.div
              className="w-5 h-0.5 bg-midnight mb-1 rounded-full"
              animate={{
                rotate: isMobileMenuOpen ? 45 : 0,
                y: isMobileMenuOpen ? 6 : 0,
              }}
              transition={{ duration: 0.3 }}
            />
            <motion.div
              className="w-5 h-0.5 bg-midnight mb-1 rounded-full"
              animate={{
                opacity: isMobileMenuOpen ? 0 : 1,
              }}
              transition={{ duration: 0.3 }}
            />
            <motion.div
              className="w-5 h-0.5 bg-midnight rounded-full"
              animate={{
                rotate: isMobileMenuOpen ? -45 : 0,
                y: isMobileMenuOpen ? -6 : 0,
              }}
              transition={{ duration: 0.3 }}
            />
          </button>
          </div>
        </div>

        {/* Mobile User Dropdown Menu */}
        <AnimatePresence>
          {isUserMenuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="md:hidden fixed top-20 right-4 w-64 bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 py-2"
                style={{ zIndex: 100 }}
              >
                {loading ? (
                  <div className="px-4 py-3 text-2 text-midnight/60 text-center">
                    Loading...
                  </div>
                ) : isAuthenticated && user ? (
                    <>
                      <div className="px-4 py-3 border-b border-midnight/10">
                        <p className="text-2 font-semibold text-midnight">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-1 text-midnight/50 truncate">{user.email}</p>
                      </div>
                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-3 text-2 font-medium text-midnight/70 hover:bg-fog transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <UserCircle className="h-4 w-4 mr-3" />
                        View Profile
                      </Link>
                      <Link
                        to="/bookings"
                        className="flex items-center px-4 py-3 text-2 font-medium text-midnight/70 hover:bg-fog transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Calendar className="h-4 w-4 mr-3" />
                        My Bookings
                      </Link>
                      <Link
                        to="/my-consultations"
                        className="flex items-center px-4 py-3 text-2 font-medium text-midnight/70 hover:bg-fog transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Video className="h-4 w-4 mr-3" />
                        My Consultations
                      </Link>
                      <div className="border-t border-midnight/10 my-1"></div>
                      <button
                        onClick={async () => {
                          setIsUserMenuOpen(false);
                          await logout();
                          navigate('/');
                        }}
                        className="w-full flex items-center px-4 py-3 text-2 font-medium text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="h-4 w-4 mr-3" />
                        Logout
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        className="block px-4 py-3 text-2 font-medium text-midnight/70 hover:bg-fog transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Login
                      </Link>
                      <Link
                        to="/signup"
                        className="block px-4 py-3 text-2 font-medium text-midnight/70 hover:bg-fog transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Sign Up
                      </Link>
                    </>
                  )}
              </motion.div>
            </>
          )}
        </AnimatePresence>


      </div>
    </nav>
    {mobileMenuPortal}
    </>
  );
};

export default Navbar; 